import React, { useRef } from 'react';
import {
	AlertProps,
	ArrowDownIcon,
	fromThemeColors,
	InfoBanner,
	layout,
	ThemeColor,
} from '@citrite/web-ui-component';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { debounce } from 'lodash';
import { BannerPosition } from 'AdminNotificationService/createAdminNotificationClient';
import { ContentProps } from 'Components/AppLayout/AdminNotificationBanner';
import { useIsDetailedDesktopTileEnabled } from 'Configuration/hooks';
import { centerContentWidthPX } from 'Workspace/dimensions';
import {
	getBannerHeight,
	getBannerHeightDetailed,
	MastheadOffset,
} from 'Workspace/ThemeProvider';

type BannerTextAlignment = 'center' | 'left' | 'right';

// This is an arbitrary value meant to offset various nested padding
// values that we get from the banner, its icon and the icon used to indicate
// that more can be shown.
const paddingAndIconOffset = 105;

const bannerColor = {
	info: fromThemeColors('infoBackground'),
	warning: fromThemeColors('warningBackground'),
	error: fromThemeColors('errorBackground'),
	success: fromThemeColors('successBackground'),
	custom: fromThemeColors('primaryAppBackground'), // Will change based on future design updates
};
interface Props {
	Content: React.ComponentType<ContentProps>;
	bannerPosition: BannerPosition;
	mastheadOffset?: MastheadOffset;
	textAlign?: BannerTextAlignment;
	isAdminBanner?: boolean;
	Banner?: React.ComponentType<AlertProps>;
	bannerType?: 'info' | 'warning' | 'error' | 'success' | 'custom';
}

const StyledLineClamp = styled.div<{
	expandedBanner: boolean;
	innerWidth: number;
	bannerPosition: BannerPosition;
}>`
	cursor: pointer;
	max-width: ${props => props.innerWidth - paddingAndIconOffset}px;
	display: inline-block;
	overflow: ${props => (props.expandedBanner ? 'visible' : 'hidden')};
	text-overflow: ellipsis;
	white-space: ${props => (props.expandedBanner ? 'normal' : 'nowrap')};
`;

const StyledDropdownIconLegacy = styled(ArrowDownIcon)<{
	expandedBanner: boolean;
	bannerPosition: BannerPosition;
}>`
	cursor: pointer;
	display: inline-block;
	position: absolute;
	right: 10px;
	${props =>
		props.bannerPosition !== BannerPosition.Bottom &&
		props.expandedBanner &&
		'transform: rotate(180deg)'};
	${props =>
		props.bannerPosition === BannerPosition.Bottom &&
		!props.expandedBanner &&
		'transform: rotate(180deg)'};
`;

const StyledDropdownIconDetailed = styled(StyledDropdownIconLegacy)`
	right: 0px;
`;

const StyledContainerLegacy = styled.div<{
	bannerPosition: BannerPosition;
	textAlign: BannerTextAlignment;
	isAdminBanner?: boolean;
	bannerType?: 'info' | 'warning' | 'error' | 'success' | 'custom';
}>`
	width: 100%;
	${props =>
		props.bannerPosition === BannerPosition.Bottom &&
		'position: fixed; bottom: 0px; z-index: 1;'};
	text-align: ${props => props.textAlign};
	> div:nth-of-type(1) {
		margin-bottom: 0;
	}
`;

const StyledContainerDetailed = styled(StyledContainerLegacy)`
	background-color: ${props =>
		props.bannerType ? bannerColor[props.bannerType] : fromThemeColors('infoBackground')};
	> div:nth-of-type(1) {
		max-width: ${centerContentWidthPX}px;
		padding: 0 ${layout.space20};
		margin: 0 auto;
		> div:nth-of-type(1) {
			padding: ${layout.space16} 0;
		}
	}
	${props =>
		!props.isAdminBanner &&
		css`
			border-bottom: 1px solid ${fromThemeColors(ThemeColor.border)(props)};
		`}
`;

export function DaasNotificationBanner(props: Props) {
	const {
		Content,
		bannerPosition,
		mastheadOffset,
		isAdminBanner,
		bannerType = 'info',
	} = props;
	const [isBannerExpanded, setBannerExpanded] = React.useState(false);
	const [displayArrowIcon, setDisplayArrowIcon] = React.useState(true);
	const [clientWidth, updateClientWidth] = React.useState(document.body.clientWidth);
	const contentRef = useRef(null);
	const isDetailedDesktopTileEnabled = useIsDetailedDesktopTileEnabled();

	// WSUI-12644: update the masthead bottom offset in an effect rather than during
	// render. DaaS-Vision (with its bottom admin banner) now renders for all stores, and
	// calling setDaasBottomOffset (a setState on ThemeProvider) during render triggers a
	// "Cannot update a component while rendering a different component" warning.
	React.useEffect(() => {
		if (bannerPosition === BannerPosition.Bottom) {
			mastheadOffset?.setDaasBottomOffset(
				isDetailedDesktopTileEnabled ? getBannerHeightDetailed() : getBannerHeight()
			);
		}
	}, [bannerPosition, isDetailedDesktopTileEnabled, mastheadOffset]);
	React.useEffect(() => {
		window.addEventListener('click', onClickOutsideBanner);
		if (!isAdminBanner) {
			window.addEventListener('resize', onWindowResize);
			checkTruncation();
		}
		return () => {
			window.removeEventListener('click', onClickOutsideBanner);
			if (!isAdminBanner) {
				window.removeEventListener('resize', onWindowResize);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const checkTruncation = () => {
		if (contentRef?.current) {
			const allContent = contentRef?.current?.scrollWidth;
			const visibleContent = contentRef?.current?.clientWidth;
			setDisplayArrowIcon(allContent > visibleContent);
		}
	};
	React.useEffect(() => {
		if (contentRef?.current) {
			checkTruncation();
		}
	}, [clientWidth]);

	const onWindowResize = debounce(() => {
		setBannerExpanded(false);
		updateClientWidth(window.document.body.clientWidth);
	}, 300);

	const onClickOutsideBanner = () => {
		setBannerExpanded(false);
	};

	const onBannerClick = (e: React.MouseEvent) => {
		if (isAdminBanner || displayArrowIcon) {
			setBannerExpanded(!isBannerExpanded);
			e.stopPropagation();
		}
	};

	const BannerComponent = props.Banner || InfoBanner;

	const StyledContainer = isDetailedDesktopTileEnabled
		? StyledContainerDetailed
		: StyledContainerLegacy;

	const StyledDropdownIcon = isDetailedDesktopTileEnabled
		? StyledDropdownIconDetailed
		: StyledDropdownIconLegacy;

	return (
		<StyledContainer
			data-testid="daas-admin-banner-container"
			bannerPosition={bannerPosition}
			textAlign={props.textAlign || 'left'}
			onClick={onBannerClick}
			isAdminBanner={isAdminBanner}
			bannerType={bannerType}
		>
			<BannerComponent>
				<StyledLineClamp
					data-testid="daas-admin-banner-line-clamp"
					innerWidth={window.innerWidth}
					expandedBanner={isBannerExpanded}
					bannerPosition={bannerPosition}
					ref={contentRef}
				>
					<Content isExpanded={isBannerExpanded} />
				</StyledLineClamp>
				{(isAdminBanner || displayArrowIcon) && (
					<StyledDropdownIcon
						data-testid="daas-admin-banner-dropdown-icon"
						role="button"
						tabIndex={0}
						expandedBanner={isBannerExpanded}
						bannerPosition={bannerPosition}
						onClick={onBannerClick}
					/>
				)}
			</BannerComponent>
		</StyledContainer>
	);
}
